import { useState, useEffect, useRef, useCallback } from "react";
import { myApi } from "../api";
import {
  Play, Pause, SkipForward, SkipBack, Volume2, VolumeX,
  Loader2, MapPin, X, RotateCcw,
} from "lucide-react";

/**
 * "Como Funciona" — Tour em Vídeo interativo do portal MY.
 * Cada cena explica uma opção do menu, com fundo de uma cidade do mundo,
 * apresentador que alterna entre mulher e homem, uma piada e narração por voz.
 * A narração (áudio) auto-avança para a cena seguinte quando termina.
 */
export default function MyComoFunciona() {
  const [scenes, setScenes] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    myApi.tourScenes()
      .then((d) => setScenes(d?.scenes || []))
      .catch(() => setScenes([]))
      .finally(() => setLoading(false));
    // Abrir o tour conta como "visto" — não voltamos a sugeri-lo.
    myApi.tourMarkSeen().catch(() => {});
  }, []);

  const scene = scenes[idx];

  const goTo = useCallback((n) => {
    if (n < 0 || n >= scenes.length) return;
    setIdx(n);
  }, [scenes.length]);

  const handleEnded = useCallback(() => {
    if (idx < scenes.length - 1) {
      setTimeout(() => setIdx((i) => i + 1), 700);
    } else {
      setPlaying(false);
    }
  }, [idx, scenes.length]);

  // Carrega e toca a narração sempre que a cena muda (ponto ÚNICO de play,
  // para não haver dois play() a competir → "play() interrupted").
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !scene || !started) return;
    if (a.src !== scene.narrationUrl && !a.src.endsWith(scene.narrationUrl)) {
      a.src = scene.narrationUrl;
    }
    a.muted = muted;
    if (playing) {
      setAudioLoading(true);
      const p = a.play();
      if (p && p.catch) p.then(() => setAudioLoading(false)).catch(() => setAudioLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, started, playing]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { setPlaying(true); }
  };

  const start = () => {
    // O useEffect acima trata do play quando `started`+`playing` ficam true.
    setStarted(true);
    setPlaying(true);
  };

  const toggleMute = () => {
    const a = audioRef.current;
    const nv = !muted;
    setMuted(nv);
    if (a) a.muted = nv;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="tour-loading">
        <Loader2 className="animate-spin text-[#0F1829]" size={32} />
      </div>
    );
  }
  if (!scenes.length) {
    return <div className="p-8 text-center text-slate-500" data-testid="tour-empty">Tour indisponível de momento.</div>;
  }

  const isFemale = scene?.gender === "f";
  const accent = isFemale ? "#E0567A" : "#2563EB";

  return (
    <div className="max-w-6xl mx-auto" data-testid="my-como-funciona">
      <audio ref={audioRef} onEnded={handleEnded} onPlaying={() => setAudioLoading(false)} preload="auto" />

      {/* Header */}
      <div className="mb-4 flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-[#0F1829]">Como Funciona</h1>
          <p className="text-sm text-slate-500">Uma volta ao mundo pelas funcionalidades do teu portal.</p>
        </div>
        <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#0F1829] text-white" data-testid="tour-progress-label">
          {idx + 1} / {scenes.length}
        </span>
      </div>

      {/* Palco estilo vídeo */}
      <div
        className="relative w-full overflow-hidden rounded-2xl shadow-2xl bg-black select-none"
        style={{ aspectRatio: "16 / 9" }}
        data-testid="tour-stage"
      >
        {/* Fundos (cross-fade) */}
        {scenes.map((s, i) => (
          <div
            key={s.id}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{
              opacity: i === idx ? 1 : 0,
              backgroundImage: `url(${s.bg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              animation: i === idx ? "tourKenBurns 18s ease-out forwards" : "none",
            }}
          />
        ))}
        {/* Escurecimento para leitura */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />

        {/* Cidade (canto sup. esq.) */}
        <div className="absolute top-4 left-4 flex items-center gap-1.5 text-white/90 text-xs sm:text-sm font-medium bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full" data-testid="tour-city">
          <MapPin size={14} /> {scene.city}
        </div>

        {/* Apresentador (cartão-retrato emoldurado) */}
        <div
          key={`p-${scene.id}`}
          className="absolute right-4 sm:right-8 bottom-14 sm:bottom-16 w-[34%] sm:w-[30%] max-w-[320px]"
          style={{ animation: "tourPresenterIn 0.7s ease-out" }}
          data-testid="tour-presenter"
        >
          <div
            className="rounded-2xl overflow-hidden shadow-2xl"
            style={{ boxShadow: `0 12px 40px rgba(0,0,0,.45)`, border: `3px solid ${accent}` }}
          >
            <img src={scene.presenter} alt="Apresentador" className="w-full aspect-square object-cover" />
          </div>
          <div className="mt-2 text-center">
            <span className="inline-block text-white text-xs sm:text-sm font-semibold px-3 py-1 rounded-full backdrop-blur-md" style={{ background: accent }}>
              {isFemale ? "Sofia" : "Miguel"}
            </span>
          </div>
        </div>

        {/* Texto */}
        {started ? (
          <div className="absolute left-0 top-0 h-full flex flex-col justify-center gap-3 p-5 sm:p-10 max-w-[64%]">
            <span
              className="inline-flex w-fit items-center gap-2 text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full text-white"
              style={{ background: accent }}
              data-testid="tour-badge"
            >
              {isFemale ? "Apresenta: Sofia" : "Apresenta: Miguel"}
            </span>
            <h2 key={`t-${scene.id}`} className="text-white text-2xl sm:text-4xl font-extrabold leading-tight"
                style={{ animation: "tourSlideUp 0.6s ease-out" }} data-testid="tour-title">
              {scene.title}
            </h2>
            <p key={`e-${scene.id}`} className="text-white/90 text-sm sm:text-lg leading-relaxed"
               style={{ animation: "tourSlideUp 0.6s ease-out 0.1s both" }} data-testid="tour-explanation">
              {scene.explanation}
            </p>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 text-center px-6">
            <h2 className="text-white text-3xl sm:text-5xl font-extrabold drop-shadow-lg">Bem-vindo ao teu portal</h2>
            <p className="text-white/85 max-w-md">Faz esta pequena viagem connosco e descobre tudo o que podes fazer no teu portal.</p>
            <button
              onClick={start}
              className="inline-flex items-center gap-2 bg-white text-[#0F1829] font-bold px-7 py-3.5 rounded-full hover:scale-105 transition-transform shadow-xl"
              data-testid="tour-start-btn"
            >
              <Play size={20} fill="#0F1829" /> Iniciar o Tour
            </button>
          </div>
        )}

        {/* Loader áudio */}
        {audioLoading && started && (
          <div className="absolute bottom-20 left-5 sm:left-10 flex items-center gap-2 text-white/80 text-xs">
            <Loader2 size={14} className="animate-spin" /> a preparar a narração…
          </div>
        )}

        {/* Barra de progresso (cenas) */}
        <div className="absolute bottom-0 left-0 right-0 flex gap-1 p-2">
          {scenes.map((s, i) => (
            <button
              key={s.id}
              onClick={() => goTo(i)}
              className="h-1.5 flex-1 rounded-full overflow-hidden bg-white/25"
              data-testid={`tour-dot-${i}`}
              aria-label={`Ir para ${s.title}`}
            >
              <span className="block h-full rounded-full transition-all" style={{ width: i < idx ? "100%" : i === idx ? "100%" : "0%", background: i <= idx ? "#fff" : "transparent" }} />
            </button>
          ))}
        </div>
      </div>

      {/* Controlos */}
      {started && (
        <div className="mt-4 flex items-center justify-center gap-3" data-testid="tour-controls">
          <button onClick={() => goTo(idx - 1)} disabled={idx === 0}
                  className="p-3 rounded-full bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40" data-testid="tour-prev">
            <SkipBack size={18} />
          </button>
          <button onClick={togglePlay}
                  className="p-4 rounded-full bg-[#0F1829] text-white hover:scale-105 transition-transform" data-testid="tour-playpause">
            {playing ? <Pause size={22} /> : <Play size={22} fill="#fff" />}
          </button>
          <button onClick={() => goTo(idx + 1)} disabled={idx === scenes.length - 1}
                  className="p-3 rounded-full bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40" data-testid="tour-next">
            <SkipForward size={18} />
          </button>
          <button onClick={toggleMute}
                  className="p-3 rounded-full bg-white border border-slate-200 hover:bg-slate-50" data-testid="tour-mute">
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          {idx === scenes.length - 1 && !playing && (
            <button onClick={() => { setIdx(0); setTimeout(start, 100); }}
                    className="ml-2 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700" data-testid="tour-restart">
              <RotateCcw size={16} /> Recomeçar
            </button>
          )}
        </div>
      )}

      {/* Lista de capítulos */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="tour-chapters">
        {scenes.map((s, i) => (
          <button
            key={s.id}
            onClick={() => { if (!started) start(); goTo(i); }}
            className={`text-left rounded-xl px-3 py-2 border transition-all ${i === idx ? "border-[#0F1829] bg-[#0F1829]/5" : "border-slate-200 hover:border-slate-300"}`}
            data-testid={`tour-chapter-${s.id}`}
          >
            <span className="block text-[11px] text-slate-400">{String(i + 1).padStart(2, "0")} · {s.city.split(",")[0]}</span>
            <span className="block text-sm font-semibold text-[#0F1829] truncate">{s.title}</span>
          </button>
        ))}
      </div>

      <style>{`
        @keyframes tourKenBurns { 0% { transform: scale(1.04) translateX(0); } 100% { transform: scale(1.15) translateX(-1.5%); } }
        @keyframes tourSlideUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes tourPresenterIn { from { opacity: 0; transform: translateX(40px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>
    </div>
  );
}
